/**
 * T4 通知中心 hooks。
 *
 * 设计原则：
 *  - 列表 / 未读计数 / 已读 都是 swr 风格的本地 cache，
 *    hook 暴露 mutate() 让 UI 在用户主动 mark read 后立刻更新 cache，
 *    避免回到列表页还要重新拉一次。
 *  - 列表分页用 cursor (next_before) 而非 offset，state 只持有累积的 items + 下一页游标；
 *    hasMore = next_before !== undefined。
 *  - 所有 hook 返回 isLoading / error 标准三态；调用方自行决定是否展示。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getUnreadCount,
  listNotifications,
  markRead,
} from './api/notifications';
import { NOTIFICATION_PAGE_SIZE } from './types';
import type { NotificationType, NotificationView } from './types';
import { readAccess } from '../../shared/api/tokens';

interface ListState {
  items: NotificationView[];
  nextBefore: number | undefined;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
}

/** 通知列表：首次加载 + loadMore + 单条 markRead 自动从 items 里去掉未读高亮。 */
export function useNotificationsList() {
  const [state, setState] = useState<ListState>({
    items: [],
    nextBefore: undefined,
    isLoading: true,
    isLoadingMore: false,
    error: null,
    hasMore: true,
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const resp = await listNotifications({ limit: NOTIFICATION_PAGE_SIZE });
      if (!mounted.current) return;
      setState({
        items: resp.data,
        nextBefore: resp.next_before,
        isLoading: false,
        isLoadingMore: false,
        error: null,
        hasMore: resp.next_before !== undefined,
      });
    } catch (err) {
      if (!mounted.current) return;
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : '列表加载失败',
      }));
    }
  }, []);

  const loadMore = useCallback(async () => {
    setState((prev) => {
      if (prev.isLoadingMore || !prev.hasMore || prev.nextBefore === undefined) {
        return prev;
      }
      // 异步分支：从闭包里取出 nextBefore / hasMore 后立刻发起请求。
      const cursor = prev.nextBefore;
      (async () => {
        setState((s) => ({ ...s, isLoadingMore: true, error: null }));
        try {
          const resp = await listNotifications({
            limit: NOTIFICATION_PAGE_SIZE,
            before: cursor,
          });
          if (!mounted.current) return;
          setState((s) => ({
            items: [...s.items, ...resp.data],
            nextBefore: resp.next_before,
            isLoading: false,
            isLoadingMore: false,
            error: null,
            hasMore: resp.next_before !== undefined,
          }));
        } catch (err) {
          if (!mounted.current) return;
          setState((s) => ({
            ...s,
            isLoadingMore: false,
            error: err instanceof Error ? err.message : '加载更多失败',
          }));
        }
      })();
      return prev;
    });
  }, []);

  const markItemRead = useCallback(async (id: string) => {
    try {
      const updated = await markRead(id);
      setState((prev) => ({
        ...prev,
        items: prev.items.map((it) => (it.id === id ? updated : it)),
      }));
      return updated;
    } catch (err) {
      // 标记失败不要清空列表 — 留给 next refresh 再尝试
      throw err;
    }
  }, []);

  return { ...state, load, loadMore, markItemRead };
}

/** 未读数；进入页面取一次 + 暴露 mutate 让首页小红点主动刷新。 */
export function useUnreadCount(type?: NotificationType) {
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    // 未登录态不请求通知；通知中心属于登录后能力，避免 401 触发全局重定向循环。
    if (!readAccess()) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const n = await getUnreadCount(type);
      if (!mounted.current) return;
      setCount(n);
    } catch (err) {
      if (!mounted.current) return;
      setError(err instanceof Error ? err.message : '未读数加载失败');
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [type]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { count, isLoading, error, refresh, setCount };
}
